import type { Event, EventHint } from '@sentry/react';
import type { AxiosError } from 'axios';

const isAxiosError = (error: unknown): error is AxiosError =>
  typeof error === 'object' && error !== null && 'isAxiosError' in error && error.isAxiosError === true;

const ID_SEGMENT = /^[0-9a-f]{8,}(-[0-9a-f]{4,}){0,4}$|^\d+$/i;
const MAX_RESPONSE_BODY = 300;

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const templateSegment = (segment: string): string => {
  const decoded = decodeSegment(segment);
  if (decoded.includes('@')) return '{email}';
  if (ID_SEGMENT.test(decoded)) return '{id}';
  return segment;
};

export const templatePath = (path: string): string => path.split('/').map(templateSegment).join('/');

const ABSOLUTE_URL = /^([a-z][a-z\d+\-.]*:)?\/\//i;
const EMAIL = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi;

const fullUrl = (url: string, baseURL?: string): string =>
  baseURL && !ABSOLUTE_URL.test(url) ? `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : url;

export const redactEmails = (value: string): string => value.replace(EMAIL, '{email}');

const scrubBreadcrumbs = (event: Event): void => {
  event.breadcrumbs?.forEach(crumb => {
    const data = crumb.data;
    if (!data) return;

    ['url', 'from', 'to'].forEach(key => {
      if (typeof data[key] === 'string') data[key] = redactEmails(data[key]);
    });
  });
};

const requestPath = (error: AxiosError): string | undefined => {
  const url = error.config?.url;
  if (!url) return undefined;

  const combined = fullUrl(url, error.config?.baseURL);
  try {
    return templatePath(new URL(combined, window.location.origin).pathname);
  } catch {
    return templatePath(combined.split('?')[0]);
  }
};

const responseBody = (error: AxiosError): string | undefined => {
  const data = error.response?.data;
  if (data === undefined || data === null) return undefined;

  const text = redactEmails(typeof data === 'string' ? data : JSON.stringify(data));
  return text.length > MAX_RESPONSE_BODY ? `${text.slice(0, MAX_RESPONSE_BODY)}…` : text;
};

export const enrichAxiosEvent = (event: Event, hint?: EventHint): Event | null => {
  scrubBreadcrumbs(event);

  const error = hint?.originalException;
  if (!isAxiosError(error)) return event;

  if (error.code === 'ERR_CANCELED') return null;

  const method = error.config?.method?.toUpperCase();
  const path = requestPath(error);
  const status = error.response?.status;
  const outcome = status ? String(status) : error.code || 'unknown';
  const body = responseBody(error);

  event.fingerprint = ['axios', method || 'unknown', path || 'unknown', outcome];

  event.tags = {
    ...event.tags,
    'http.method': method,
    'http.path': path,
    'http.status_code': status,
    'axios.code': error.code,
  };

  if (body !== undefined) {
    event.extra = { ...event.extra, 'http.response_body': body };
  }

  const value = event.exception?.values?.[0];
  if (value && method && path) {
    value.value = `${method} ${path} -> ${outcome}`;
  }

  return event;
};
